(() => {
  'use strict';

  const CONFIG = {
    sectionId: 'tpvc-cart-delivery-data',
    hostSelector: 'section-store--cart-block',
    lineSelector: '.cart-line-content',
    outputClass: 'tpvc-cart-line-dispatch',
    mutationDelay: 50,
    cartActionDelay: 150
  };

  let shadowRoot = null;
  let shadowObserver = null;
  let hostObserver = null;
  let mutationTimer = null;
  let cartActionTimer = null;
  let activeRequest = null;

  let deliveryCache = new Map();
  let lastFetchedSignature = null;

  function getCartLines() {
    if (!shadowRoot) return [];

    return Array.from(
      shadowRoot.querySelectorAll(CONFIG.lineSelector)
    ).map((element) => {
      const keyElement = element.querySelector('[data-line-key]');
      const quantityInput = element.querySelector(
        '[data-line-quantity-input]'
      );

      return {
        element,
        key: keyElement?.dataset.lineKey || '',
        quantity:
          quantityInput?.value ||
          keyElement?.dataset.lineQuantity ||
          ''
      };
    }).filter((line) => line.key);
  }

  function getSignature(lines) {
    return lines
      .map((line) => `${line.key}:${line.quantity}`)
      .join('|');
  }

function updatePriceSavings() {
  if (!shadowRoot) return;

  shadowRoot.querySelectorAll('.cart-line-price').forEach((price) => {
    const compare = price.querySelector('.cart-line-compare');
    let saving = price.querySelector('.tpvc-cart-line-saving');

    const current = Array.from(price.children).find((element) => {
      return (
        element.tagName === 'SPAN' &&
        !element.classList.contains('cart-line-compare') &&
        !element.classList.contains('tpvc-cart-line-saving')
      );
    });

    function parsePence(element) {
      const text = element?.textContent.trim() || '';

      // Match the GBP format shown in your cart.
      const match = text.match(/£\s*([\d,]+(?:\.\d{1,2})?)/);

      if (!match) return null;

      return Math.round(
        Number(match[1].replace(/,/g, '')) * 100
      );
    }

    const originalPence = parsePence(compare);
    const currentPence = parsePence(current);

    if (
      originalPence === null ||
      currentPence === null ||
      originalPence <= currentPence
    ) {
      saving?.remove();
      return;
    }

    const amount = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format((originalPence - currentPence) / 100);

    const label = `- ${amount}`;

    if (!saving) {
      saving = document.createElement('span');
    saving.className = 'cart-line-compare tpvc-cart-line-saving';
    saving.style.cssText =
      'color:#5c8c5a;text-decoration:none;';
      saving.textContent = label;
      compare.insertAdjacentElement('afterend', saving);
    } else if (saving.textContent !== label) {
      saving.textContent = label;
    }
  });
}

// Keep the original discount text so tiers can be recalculated
// after quantity changes, even after we have rewritten the label.
// Remember the original label after replacing its visible text.
const discountLabelTextCache = new WeakMap();

function updateDiscountLabels() {
  if (!shadowRoot) return;

  const discountGroups = new Map();
  const labelsToUpdate = [];

  shadowRoot
    .querySelectorAll('.cart-line-discounts > span')
    .forEach((label) => {
      const line = label.closest(CONFIG.lineSelector);
      if (!line) return;

      const quantityInput = line.querySelector(
        '[data-line-quantity-input]'
      );

      const keyElement = line.querySelector('[data-line-key]');

      const quantity = Number(
        quantityInput?.value ||
        keyElement?.dataset.lineQuantity ||
        ''
      );

      if (!Number.isFinite(quantity) || quantity < 1) return;

      const textNodes = Array.from(label.childNodes)
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => {
          const currentText = node.textContent || '';
          let cached = discountLabelTextCache.get(node);

          // Accept new text supplied by the app, but retain
          // the original when the current text is our own output.
          if (!cached || currentText !== cached.rendered) {
            cached = {
              original: currentText,
              rendered: currentText
            };

            discountLabelTextCache.set(node, cached);
          }

          return { node, cached };
        });

      const originalLabel = textNodes
        .map(({ cached }) => cached.original)
        .join('');

      const tierPattern =
        /\b(?:DE-)?\d+:\d+(?:\.\d+)?(?:\s*\|\s*(?:DE-)?\d+:\d+(?:\.\d+)?)*\b/gi;

      if (!tierPattern.test(originalLabel)) return;

      // Use the complete original discount label as the group key,
      // not just its numeric thresholds.
      const groupKey = originalLabel
        .replace(/\s+/g, ' ')
        .trim();

      if (!discountGroups.has(groupKey)) {
        discountGroups.set(groupKey, new Map());
      }

      // Count each cart line only once within this discount group.
      discountGroups.get(groupKey).set(line, quantity);

      labelsToUpdate.push({
        groupKey,
        textNodes
      });
    });

  const groupQuantities = new Map();

  discountGroups.forEach((lines, groupKey) => {
    const total = Array.from(lines.values())
      .reduce((sum, quantity) => sum + quantity, 0);

    groupQuantities.set(groupKey, total);
  });

  labelsToUpdate.forEach(({ groupKey, textNodes }) => {
    const quantity = groupQuantities.get(groupKey) || 0;

    textNodes.forEach(({ node, cached }) => {
      const updated = cached.original.replace(
        /\b(?:DE-)?\d+:\d+(?:\.\d+)?(?:\s*\|\s*(?:DE-)?\d+:\d+(?:\.\d+)?)*\b/gi,
        (discountText) => {
          const tiers = discountText
            .split('|')
            .map((tier) => {
              const match = tier.trim().match(
                /^(?:DE-)?(\d+):(\d+(?:\.\d+)?)$/i
              );

              return match
                ? {
                    minimum: Number(match[1]),
                    percentage: Number(match[2])
                  }
                : null;
            })
            .filter(Boolean)
            .sort((a, b) => a.minimum - b.minimum);

          // Keep the original single-tier behaviour.
          if (tiers.length === 1) {
            const tier = tiers[0];

            return `Save ${tier.percentage}% on ${tier.minimum} or more`;
          }

          const eligibleTier = tiers
            .filter((tier) => quantity >= tier.minimum)
            .pop();

          if (!eligibleTier) return '';

          return `Save ${eligibleTier.percentage}% on ${eligibleTier.minimum} or more`;
        }
      );

      cached.rendered = updated;

      // Preserve the SVG and avoid unnecessary observer updates.
      if (node.textContent !== updated) {
        node.textContent = updated;
      }
    });
  });
}


function applyMessages(lines, allowRemoval = false) {
  updatePriceSavings();
  updateDiscountLabels();

  lines.forEach((line) => {
      const html = deliveryCache.get(line.key);
      let output = line.element.querySelector(
        `.${CONFIG.outputClass}`
      );

      if (!html) {
        if (allowRemoval) output?.remove();
        return;
      }

      if (!output) {
        output = document.createElement('div');
        output.className =
          `cart-item__dispatch ${CONFIG.outputClass}`;

        // Populate before inserting, avoiding an empty intermediate node.
        output.innerHTML = html;

        const anchor =
          line.element.querySelector('.cart-line-subtitle') ||
          line.element.querySelector('.cart-line-title');

        if (anchor) {
          anchor.insertAdjacentElement('afterend', output);
        } else {
          line.element.prepend(output);
        }
      } else if (output.innerHTML !== html) {
        output.innerHTML = html;
      }
    });
  }

  function fetchDeliveryMessages() {
    // Share an in-flight request rather than repeatedly aborting it.
    if (activeRequest) return activeRequest;

    const requestedSignature = shadowRoot
      ? getSignature(getCartLines())
      : null;

    const root = window.Shopify?.routes?.root || '/';
    const url =
      `${root}cart?section_id=` +
      encodeURIComponent(CONFIG.sectionId);

    activeRequest = (async () => {
      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'text/html' }
        });

        if (!response.ok) {
          throw new Error(
            `Delivery section request failed: ${response.status}`
          );
        }

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const nextCache = new Map();

        documentFragment.querySelectorAll(
          'template[data-tpvc-delivery-key]'
        ).forEach((template) => {
          const key = template.dataset.tpvcDeliveryKey;

          if (key) {
            nextCache.set(key, template.innerHTML.trim());
          }
        });

        deliveryCache = nextCache;

        const lines = getCartLines();
        const currentSignature = getSignature(lines);

        // Only treat the response as current if the drawer did not
        // change while the request was running.
        const unchanged =
          requestedSignature !== null &&
          requestedSignature === currentSignature;

        applyMessages(lines, unchanged);
        lastFetchedSignature = unchanged
          ? currentSignature
          : null;

        if (shadowRoot && !unchanged && lines.length) {
          scheduleRefresh();
        }
      } catch (error) {
        console.warn(
          'TPVC cart delivery estimates could not be loaded.',
          error
        );
      } finally {
        activeRequest = null;
      }
    })();

    return activeRequest;
  }

  function refresh(force = false) {
    const lines = getCartLines();

    // Always restore cached content before considering a request.
    applyMessages(lines);

    if (!lines.length) return;

    const signature = getSignature(lines);

    if (!force && signature === lastFetchedSignature) return;

    fetchDeliveryMessages();
  }

  function scheduleRefresh() {
    window.clearTimeout(mutationTimer);

    mutationTimer = window.setTimeout(() => {
      refresh();
    }, CONFIG.mutationDelay);
  }

  function scheduleCartActionRefresh() {
    window.clearTimeout(cartActionTimer);

    cartActionTimer = window.setTimeout(() => {
      refresh(true);
    }, CONFIG.cartActionDelay);
  }

  function handleClick(event) {
    if (event.target instanceof Element &&
        event.target.closest('[data-line-action]')) {
      scheduleCartActionRefresh();
    }
  }

  function handleChange(event) {
    if (event.target instanceof Element &&
        event.target.matches('[data-line-quantity-input]')) {
      scheduleCartActionRefresh();
    }
  }

  function connectToCart() {
    const host = document.querySelector(CONFIG.hostSelector);
    const root = host?.shadowRoot;

    if (!root) return false;
    if (root === shadowRoot) return true;

    if (shadowRoot) {
      shadowRoot.removeEventListener('click', handleClick);
      shadowRoot.removeEventListener('change', handleChange);
    }

    shadowObserver?.disconnect();
    shadowRoot = root;

    shadowObserver = new MutationObserver(() => {
      // Observer callbacks run before the next paint.
      // Restore existing messages without waiting for a timer.
      applyMessages(getCartLines());
      scheduleRefresh();
    });

    shadowObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-line-key', 'data-line-quantity']
    });

    root.addEventListener('click', handleClick);
    root.addEventListener('change', handleChange);

    refresh();
    return true;
  }

  function initialise() {
    connectToCart();

    // Keep watching in case the app replaces its cart component.
    hostObserver = new MutationObserver(connectToCart);
    hostObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    window.addEventListener('pageshow', () => {
      connectToCart();
      refresh(true);
    });
  }

  // Start loading delivery HTML immediately, even before the
  // app's cart component or line items are available.
  fetchDeliveryMessages();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, {
      once: true
    });
  } else {
    initialise();
  }
})();

